import type { ModuleTone } from '../ui/ModuleSection';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: string;
  /** DESIGN.md §Navigation: an active tab wears its module's pastel chip. */
  module?: ModuleTone;
  end?: boolean;
}

/**
 * The one navigation table. The bar and the rail are two expressions of it, so
 * a destination cannot exist in one and not the other - which is how
 * `AppShell` ended up rendering the same eight items three times over.
 *
 * **DESIGN.md §Layout: six.** The bar is designed for six items at 390px, in
 * French, with a 44px hit area each. A seventh does not get appended here; it
 * replaces one, or it belongs behind the profile menu with the management
 * screens.
 *
 * `/dashboard/calendar` rather than `/dashboard/events`: the events route is a
 * redirect to it (App.tsx), and pointing a tab at a redirect makes the active
 * state flicker on the way through.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard/announcements',
    labelKey: 'nav.announcements',
    icon: 'campaign',
    module: 'announcements',
  },
  { to: '/dashboard/complaints', labelKey: 'nav.complaints', icon: 'report', module: 'complaints' },
  { to: '/dashboard/calendar', labelKey: 'nav.calendar', icon: 'calendar_today', module: 'events' },
  {
    to: '/dashboard/restaurant',
    labelKey: 'nav.restaurant',
    icon: 'restaurant',
    module: 'restaurant',
  },
  { to: '/dashboard/library', labelKey: 'nav.library', icon: 'local_library', module: 'library' },
  {
    to: '/dashboard/carpooling',
    labelKey: 'nav.carpooling',
    icon: 'directions_car',
    module: 'carpooling',
  },
];

/**
 * Not module destinations: screens a role reaches, shown on the desktop rail
 * and in the profile menu. They are deliberately absent from the phone bar -
 * see the six-item note above.
 */
export const MANAGEMENT_ITEMS: NavItem[] = [
  { to: '/dashboard/admin/users', labelKey: 'nav.users', icon: 'group' },
];

/**
 * Where a sign-in, a verification, a refused permission and every fallback
 * land. It is the first tab above and not a screen of its own: the dashboard
 * it replaced was nine centred cards repeating this very table, which made the
 * first screen after a sign-in the only screen in the product with nothing on
 * it. One constant so the six places that used to say `/dashboard` cannot
 * drift apart again.
 */
export const HOME = '/dashboard/announcements';
